import type {
  AdminMemberCodeItem,
  AdminReferralItem,
  MemberReferralStatus,
} from '@yoyoclub/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';

import AdminLayout from '../../components/admin/AdminLayout';
import { memberRewardsApi } from '../../services/memberRewardsApi';

/**
 * /admin/member-rewards (docs/prd-member-rewards.md §7): every order that used a
 * member's promo code, and the member codes themselves with pause / resume.
 * Member codes never appear on the Promos page.
 */

type Tab = 'referrals' | 'codes';

const STATUS_LABEL: Record<MemberReferralStatus, string> = {
  IN_PROGRESS: 'In progress',
  REWARDED: 'Rewarded',
  NOT_COMPLETED: 'Not completed',
};

const STATUS_CLASS: Record<MemberReferralStatus, string> = {
  IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
  REWARDED: 'bg-green-100 text-green-800',
  NOT_COMPLETED: 'bg-gray-100 text-gray-600',
};

const PAGE_SIZE = 20;

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-US') : '');

function Person({ p }: { p: { name: string | null; email: string } }) {
  return (
    <div>
      <div className="font-medium text-gray-900">{p.name || p.email}</div>
      {p.name && <div className="text-xs text-gray-500">{p.email}</div>}
    </div>
  );
}

function Pager({
  page,
  total,
  loading,
  onPage,
}: {
  page: number;
  total: number;
  loading: boolean;
  onPage: (p: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (pages <= 1) return null;
  return (
    <div className="mt-4 flex items-center gap-3 text-sm">
      <button
        type="button"
        className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40"
        disabled={page <= 1 || loading}
        onClick={() => onPage(page - 1)}
      >
        Newer
      </button>
      <span className="text-gray-600">
        Page {page} of {pages}
      </span>
      <button
        type="button"
        className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40"
        disabled={page >= pages || loading}
        onClick={() => onPage(page + 1)}
      >
        Older
      </button>
    </div>
  );
}

function ReferralsTab() {
  const [status, setStatus] = useState<MemberReferralStatus | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<AdminReferralItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    memberRewardsApi
      .adminReferrals({ status, search, page })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [status, search, page]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <>
      <form onSubmit={submit} className="mb-4 flex flex-wrap items-center gap-2">
        <select
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value as MemberReferralStatus | '');
          }}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          aria-label="Status"
        >
          <option value="">All statuses</option>
          <option value="IN_PROGRESS">In progress</option>
          <option value="REWARDED">Rewarded</option>
          <option value="NOT_COMPLETED">Not completed</option>
        </select>
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search member or new user email, or code"
          className="min-w-[260px] flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
        <button type="submit" className="rounded-md bg-primary px-4 py-2 text-sm text-white">
          Search
        </button>
      </form>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {loading ? (
        <p className="text-gray-600">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-gray-600">No orders have used a new user promo code yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3">New user</th>
                <th className="px-4 py-3">Used on</th>
                <th className="px-4 py-3">Discount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Ordered</th>
                <th className="px-4 py-3">Rewarded</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 align-top">
              {items.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3">
                    <Person p={r.referrer} />
                    <div className="font-mono text-xs text-gray-500">{r.code}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Person p={r.friend} />
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    <div>{r.target.titleCN || r.target.titleEN}</div>
                    <div className="text-xs text-gray-500">
                      {r.target.kind === 'course'
                        ? 'Recorded course'
                        : r.target.kind === 'pass'
                          ? 'Pass bought'
                          : 'Session'}{' '}
                      {when(r.target.at)}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">{money(r.discountAmount)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[r.status]}`}
                    >
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600">{when(r.createdAt)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                    {when(r.rewardedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pager page={page} total={total} loading={loading} onPage={setPage} />
    </>
  );
}

function CodesTab() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<AdminMemberCodeItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await memberRewardsApi.adminCodes({ search, page });
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (item: AdminMemberCodeItem) => {
    const next = !item.isActive;
    const verb = next ? 'Resume' : 'Pause';
    if (!window.confirm(`${verb} ${item.code} (${item.owner.email})?`)) return;
    setBusyId(item.id);
    try {
      await memberRewardsApi.adminSetCodeActive(item.id, next);
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, isActive: next } : i)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setSearch(searchInput.trim());
        }}
        className="mb-4 flex flex-wrap items-center gap-2"
      >
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search member email or code"
          className="min-w-[260px] flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
        <button type="submit" className="rounded-md bg-primary px-4 py-2 text-sm text-white">
          Search
        </button>
      </form>
      <p className="mb-4 text-sm text-gray-600">
        Pausing a code stops new orders from using it and holds any reward it has not paid yet.
        Resuming it pays held rewards at the next check.
      </p>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {loading ? (
        <p className="text-gray-600">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-gray-600">No new user promo codes found.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3">Orders</th>
                <th className="px-4 py-3">Rewards paid</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 align-top">
              {items.map((c) => (
                <tr key={c.id}>
                  <td className="whitespace-nowrap px-4 py-3 font-mono">{c.code}</td>
                  <td className="px-4 py-3">
                    <Person p={c.owner} />
                  </td>
                  <td className="px-4 py-3">{c.usedCount}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {money(c.rewardedAmount)} ({c.rewardedCount})
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600">{when(c.createdAt)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        c.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {c.isActive ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => toggle(c)}
                      disabled={busyId === c.id}
                      className="rounded-md border border-gray-300 px-3 py-1 text-xs hover:bg-gray-50 disabled:opacity-50"
                    >
                      {c.isActive ? 'Pause' : 'Resume'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pager page={page} total={total} loading={loading} onPage={setPage} />
    </>
  );
}

export default function AdminMemberRewards() {
  const [tab, setTab] = useState<Tab>('referrals');
  return (
    <AdminLayout title="Member Rewards">
      <div className="mb-6 flex gap-6">
        {(
          [
            ['referrals', 'Invites'],
            ['codes', 'New user promo codes'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`relative py-2 text-sm font-semibold ${
              tab === key ? 'text-primary' : 'text-gray-600 hover:text-primary'
            }`}
          >
            {label}
            {tab === key && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
          </button>
        ))}
      </div>
      {tab === 'referrals' ? <ReferralsTab /> : <CodesTab />}
    </AdminLayout>
  );
}
